import { useQuery } from "react-query";
import { config } from "../config";
import {
  SuperAdminDashboardResponse,
  Report,
  ReportRequestParams,
  KPIReportResponse,
} from "../types";
import { client } from "./axios";

const buildReportsUrl = (
  pathTemplate: string,
  reportRequestParams: ReportRequestParams,
): string => {
  const { orgId, accountId, filters } = reportRequestParams;

  if (
    pathTemplate === config.api.reportService.getWashDetailsReport ||
    pathTemplate === config.api.reportService.getKPIReport
  ) {
    let path =
      pathTemplate
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString()) +
      `?startDate=${filters.fromDate}&endDate=${filters.toDate}`;

    if (filters.locationNumber) {
      for (const locationNumber of filters.locationNumber) {
        path += `&locationNumbers=${locationNumber}`;
      }
    }

    return path;
  } else {
    const path = pathTemplate
      .replace("{orgId}", orgId.toString())
      .replace("{accountId}", accountId.toString())
      .replace("{startDate}", filters.fromDate || "")
      .replace("{endDate}", filters.toDate || "");

    return path;
  }
};

export const useFetchSuperAdminDashboardQuery = (
  orgId: number,
  startDate?: string,
  endDate?: string,
  fetchOnMount: boolean = true,
) => {
  const fetchSuperAdminDashboard =
    async (): Promise<SuperAdminDashboardResponse> => {
      let path = config.api.reportService.getSuperAdminDashboard.replace(
        "{orgId}",
        orgId.toString(),
      );
      if (startDate && endDate) {
        path += `?startDate=${startDate}&endDate=${endDate}`;
      }
      const response = await client.get<any>(path);
      return response.data.response;
    };

  return useQuery(
    ["fetchSuperAdminDashboard", orgId, startDate, endDate],
    fetchSuperAdminDashboard,
    {
      retry: false,
      enabled: fetchOnMount,
    },
  );
};

export const useFetchUserDashboardQuery = (
  orgId: number,
  accountId: number,
  startDate?: string,
  endDate?: string,
  fetchOnMount: boolean = true,
) => {
  const fetchUserDashboard = async (): Promise<SuperAdminDashboardResponse> => {
    let path = config.api.reportService.getUserDashboard
      .replace("{orgId}", orgId.toString())
      .replace("{accountId}", accountId.toString());
    if (startDate && endDate) {
      path += `?startDate=${startDate}&endDate=${endDate}`;
    }
    const response = await client.get<any>(path);
    return response.data.response;
  };

  return useQuery(
    ["fetchUserDashboard", orgId, accountId, startDate, endDate],
    fetchUserDashboard,
    {
      retry: false,
      enabled: fetchOnMount,
    },
  );
};

export const useFetchAccountActivityReportQuery = (
  reportRequestParams: ReportRequestParams,
  fetchOnMount: boolean = true,
) => {
  const fetchAccountActivityReport = async (): Promise<Report[]> => {
    const path = buildReportsUrl(
      config.api.reportService.getAccountActivityReport,
      reportRequestParams,
    );
    const response = await client.get<any>(path);
    return response.data.response;
  };

  return useQuery(
    ["fetchAccountActivityReport", reportRequestParams],
    fetchAccountActivityReport,
    {
      retry: false,
      enabled: fetchOnMount,
    },
  );
};

export const useFetchWashDetailsReportQuery = (
  reportRequestParams: ReportRequestParams,
  fetchOnMount: boolean = true,
) => {
  const fetchWashDetailsReport = async (): Promise<Report[]> => {
    const path = buildReportsUrl(
      config.api.reportService.getWashDetailsReport,
      reportRequestParams,
    );
    const response = await client.get<any>(path);
    return response.data.response;
  };

  return useQuery(
    ["fetchWashDetailsReport", reportRequestParams],
    fetchWashDetailsReport,
    {
      retry: false,
      enabled: fetchOnMount,
    },
  );
};

export const useFetchDownloadWashDetailsReportQuery = (
  reportRequestParams: ReportRequestParams,
  fetchOnMount: boolean = true,
) => {
  const fetchDownloadWashDetailsReport = async (): Promise<ArrayBuffer> => {
    const path = buildReportsUrl(
      config.api.reportService.getWashDetailsPdfReport,
      reportRequestParams,
    );
    const response = await client.get<any>(path, {
      headers: {
        "Content-Type": "application/pdf",
        Accept: "application/pdf",
      },
      responseType: "arraybuffer",
      responseEncoding: "binary",
    });
    return response.data;
  };

  return useQuery(
    ["fetchDownloadWashDetailsReport", reportRequestParams],
    fetchDownloadWashDetailsReport,
    {
      retry: false,
      enabled: fetchOnMount,
    },
  );
};

export const useFetchLeadReportQuery = (
  reportRequestParams: ReportRequestParams,
  fetchOnMount: boolean = true,
) => {
  const fetchLeadReport = async (): Promise<Report[]> => {
    const path = buildReportsUrl(
      config.api.reportService.getLeadReport,
      reportRequestParams,
    );
    const response = await client.get<any>(path);
    return response.data.response;
  };

  return useQuery(["fetchLeadReport", reportRequestParams], fetchLeadReport, {
    retry: false,
    enabled: fetchOnMount,
  });
};

export const useFetchKPIReportQuery = (
  reportRequestParams: ReportRequestParams,
  fetchOnMount: boolean = true,
) => {
  const fetchKPIReport = async (): Promise<KPIReportResponse> => {
    const path = buildReportsUrl(
      config.api.reportService.getKPIReport,
      reportRequestParams,
    );
    const response = await client.get<any>(path);
    return response.data.response;
  };

  return useQuery(["fetchKPIReport", reportRequestParams], fetchKPIReport, {
    retry: false,
    enabled: fetchOnMount,
  });
};
